import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  AlertTitle,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Language as LanguageIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { UserProfile, UpdatePasswordData, UpdateProfileData } from '../../types/auth';
import authApi from '../../api/authApi';
import { useAuth } from '../../contexts/AuthContext';
import CalendarIntegration from '../../components/calendar/CalendarIntegration';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'hi', name: 'हिंदी' },
];

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  // Fetch latest profile data on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const profile = await authApi.getProfile();
        
        // Update form with fetched data
        profileForm.setValues({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          phone: profile.phone || '',
        });
        
        // Convert to AuthUser and update context
        const { userProfileToAuthUser } = await import('../../utils/user');
        const updatedAuthUser = userProfileToAuthUser(profile);
        await updateUser(updatedAuthUser);
        
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Update form values when user data changes (fallback)
  useEffect(() => {
    if (user && !isLoading) {
      profileForm.setValues({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
      });
    }
  }, [user, isLoading]);
  


  // Profile form
  const profileForm = useFormik<UpdateProfileData>({
    enableReinitialize: true,
    initialValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',

    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      phone: Yup.string().matches(
        /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/,
        'Enter a valid phone number'
      ),

    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setError('');
        
        // Update profile via API
        const updatedUserProfile = await authApi.updateProfile(values);
        // Convert to AuthUser for context
        const { userProfileToAuthUser } = await import('../../utils/user');
        const updatedAuthUser = userProfileToAuthUser(updatedUserProfile);
        // Update auth context with correct type
        await updateUser(updatedAuthUser);
        
        setSuccessMessage('Profile updated successfully');
        setIsEditing(false);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
        console.error('Error updating profile:', err);
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Password form
  const passwordForm = useFormik<UpdatePasswordData>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required('Current password is required'),
      newPassword: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('New password is required')
        .notOneOf(
          [Yup.ref('currentPassword')],
          'New password must be different from current password'
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'Passwords must match')
        .required('Please confirm your new password'),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        setError('');
        
        // Change password via API
        await authApi.changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });
        
        setSuccessMessage('Password changed successfully');
        resetForm();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to change password. Please try again.');
        console.error('Error changing password:', err);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleEditClick = () => {
    setIsEditing(true);
    setSuccessMessage('');
    setError('');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    profileForm.resetForm();
    setSuccessMessage('');
    setError('');
  };

  const handleCloseAlert = () => {
    setSuccessMessage('');
    setError('');
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        My Profile
      </Typography>
      
      {/* Success/Error Alerts */}
      <Snackbar
        open={!!successMessage || !!error}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <div>
          {successMessage && (
            <Alert onClose={handleCloseAlert} severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}
          {error && (
            <Alert onClose={handleCloseAlert} severity="error">
              {error}
            </Alert>
          )}
        </div>
      </Snackbar>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Avatar
            sx={{ width: 80, height: 80, fontSize: 32, mr: 3, bgcolor: 'primary.main' }}
          >
            {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0) || user?.username?.charAt(0) || 'U'}
          </Avatar>
          <Box>
            <Typography variant="h5">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography color="textSecondary">
              {typeof user?.role === 'object' ? user.role.name : user?.role || 'No role'} • {user?.email}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label="Profile" />
        <Tab label="Security" />
      </Tabs>



      {activeTab === 0 ? (
        // Profile Tab
        <>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Personal Information</Typography>
            {!isEditing ? (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
              >
                Edit Profile
              </Button>
            ) : (
              <Box>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  sx={{ mr: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={() => profileForm.handleSubmit()}
                  disabled={isLoading || !profileForm.dirty}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
              </Box>
            )}
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={profileForm.values.firstName}
                onChange={profileForm.handleChange}
                onBlur={profileForm.handleBlur}
                error={profileForm.touched.firstName && Boolean(profileForm.errors.firstName)}
                helperText={profileForm.touched.firstName && profileForm.errors.firstName}
                disabled={!isEditing || isLoading}
                InputProps={{
                  startAdornment: (
                    <PersonIcon color="action" sx={{ mr: 1 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={profileForm.values.lastName}
                onChange={profileForm.handleChange}
                onBlur={profileForm.handleBlur}
                error={profileForm.touched.lastName && Boolean(profileForm.errors.lastName)}
                helperText={profileForm.touched.lastName && profileForm.errors.lastName}
                disabled={!isEditing || isLoading}
                InputProps={{
                  startAdornment: (
                    <PersonIcon color="action" sx={{ mr: 1 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={user?.email || ''}
                disabled
                InputProps={{
                  startAdornment: (
                    <EmailIcon color="action" sx={{ mr: 1 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={profileForm.values.phone}
                onChange={profileForm.handleChange}
                onBlur={profileForm.handleBlur}
                error={profileForm.touched.phone && Boolean(profileForm.errors.phone)}
                helperText={profileForm.touched.phone && profileForm.errors.phone}
                disabled={!isEditing || isLoading}
                InputProps={{
                  startAdornment: (
                    <PhoneIcon color="action" sx={{ mr: 1 }} />
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>
        
        {/* Google Calendar Integration */}
        <CalendarIntegration />
        </>
      ) : (
        // Security Tab
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Change Password
          </Typography>
          <form onSubmit={passwordForm.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  name="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.values.currentPassword}
                  onChange={passwordForm.handleChange}
                  onBlur={passwordForm.handleBlur}
                  error={
                    passwordForm.touched.currentPassword &&
                    Boolean(passwordForm.errors.currentPassword)
                  }
                  helperText={
                    passwordForm.touched.currentPassword &&
                    passwordForm.errors.currentPassword
                  }
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <LockIcon color="action" sx={{ mr: 1 }} />
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                          edge="end"
                        >
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="New Password"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.values.newPassword}
                  onChange={passwordForm.handleChange}
                  onBlur={passwordForm.handleBlur}
                  error={
                    passwordForm.touched.newPassword &&
                    Boolean(passwordForm.errors.newPassword)
                  }
                  helperText={
                    passwordForm.touched.newPassword &&
                    passwordForm.errors.newPassword
                  }
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <LockIcon color="action" sx={{ mr: 1 }} />
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.values.confirmPassword}
                  onChange={passwordForm.handleChange}
                  onBlur={passwordForm.handleBlur}
                  error={
                    passwordForm.touched.confirmPassword &&
                    Boolean(passwordForm.errors.confirmPassword)
                  }
                  helperText={
                    passwordForm.touched.confirmPassword &&
                    passwordForm.errors.confirmPassword
                  }
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <LockIcon color="action" sx={{ mr: 1 }} />
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isLoading || !passwordForm.dirty}
                  startIcon={
                    isLoading ? <CircularProgress size={24} /> : <SaveIcon />
                  }
                >
                  {isLoading ? 'Updating...' : 'Update Password'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}
    </Box>
  );
};

export default ProfilePage;
